export const VISUAL_LANGUAGES = ['mermaid', 'brain-graph', 'brain-links'] as const;
export type VisualLanguage = typeof VISUAL_LANGUAGES[number];
export function visualLanguage(className?: string): VisualLanguage | null {
  const language = /(?:^|\s)language-([\w-]+)/.exec(className ?? '')?.[1];
  return VISUAL_LANGUAGES.includes(language as VisualLanguage) ? language as VisualLanguage : null;
}
export function hasDocumentVisuals(content: string): boolean {
  return /^ {0,3}(?:`{3,}|~{3,})(?:mermaid|brain-graph|brain-links)\s*$/m.test(content);
}

export type DocumentLink = { kind: 'external' | 'route' | 'anchor' | 'file'; target: string };
/** One navigation policy for Markdown, cards and graph nodes. Never execute document URLs. */
export function documentLink(href: string, documentKey?: string): DocumentLink | null {
  if (!href || /[\u0000-\u0020\\]/.test(href) || href.startsWith('//')) return null;
  if (/^https?:\/\//i.test(href)) {
    try {
      const url = new URL(href);
      if (url.username || url.password) return null;
      return { kind: 'external', target: url.href };
    } catch { return null; }
  }
  if (/^(mailto|tel):[^\s]+$/i.test(href)) return { kind: 'external', target: href };
  if (href.startsWith('#')) return { kind: 'anchor', target: href };
  if (href.startsWith('/')) return { kind: 'route', target: href };
  if (/^[a-z][\w+.-]*:/i.test(href)) return null;
  if (!documentKey) return null;
  let decoded: string;
  try { decoded = decodeURIComponent(href.split(/[?#]/)[0]); } catch { return null; }
  if (!decoded || /[\u0000-\u001f\\]/.test(decoded) || decoded.startsWith('/') || /^[a-z][\w+.-]*:/i.test(decoded)) return null;
  const root = documentKey.split('/').slice(0, 2).join('/') + '/';
  const parts = /^(projects|customers|library|canon|departments|dept)\//.test(decoded)
    ? [] : documentKey.split('/').slice(0, -1);
  for (const part of decoded.split('/')) {
    if (!part || part === '.') continue;
    if (part === '..') parts.pop(); else parts.push(part);
  }
  const target = parts.join('/');
  return target.startsWith(root) && target !== documentKey ? { kind: 'file', target } : null;
}

export type GraphKind = 'service' | 'document' | 'issue' | 'milestone';
export interface DocumentGraphNode {
  id: string;
  label: string;
  detail?: string;
  kind: GraphKind;
  href?: string;
  position?: { x: number; y: number };
}
export interface DocumentGraph {
  title: string;
  description?: string;
  nodes: DocumentGraphNode[];
  edges: Array<{ source: string; target: string; label?: string }>;
}
export interface DocumentResource {
  title: string;
  href: string;
  description?: string;
  issueKey?: string;
}
export interface DocumentResources { title: string; items: DocumentResource[] }

function record(value: unknown, label: string): Record<string, unknown> {
  if (!value || typeof value !== 'object' || Array.isArray(value)) throw new Error(`${label} must be an object.`);
  return value as Record<string, unknown>;
}
function text(value: unknown, label: string, optional = false, limit = 1000): string | undefined {
  if (optional && value == null) return undefined;
  if (typeof value !== 'string' || !value.trim() || value.length > limit) throw new Error(`${label} must be text (up to ${limit} characters).`);
  return value.trim();
}
function parse(source: string): Record<string, unknown> {
  if (source.length > 100_000) throw new Error('This visual is too large. Split it into smaller sections.');
  try { return record(JSON.parse(source), 'Visual'); } catch (e) {
    if (e instanceof SyntaxError) throw new Error('Check the JSON syntax in this visual.');
    throw e;
  }
}
function link(value: unknown, documentKey?: string): string | undefined {
  const href = text(value, 'Link', true, 2000);
  if (href && !documentLink(href, documentKey)) throw new Error('Use an https link, a Brain page, or a file in this document’s scope.');
  return href;
}
export function parseDocumentGraph(source: string, documentKey?: string): DocumentGraph {
  const value = parse(source);
  if (!Array.isArray(value.nodes) || !value.nodes.length || value.nodes.length > 60) throw new Error('A graph needs 1–60 nodes.');
  if (!Array.isArray(value.edges) || value.edges.length > 120) throw new Error('A graph needs an edges array with at most 120 connections.');
  const ids = new Set<string>();
  const nodes = value.nodes.map(raw => {
    const n = record(raw, 'Node');
    const id = text(n.id, 'Node id', false, 100)!;
    if (ids.has(id)) throw new Error(`Duplicate node id: ${id}`);
    ids.add(id);
    const kind = n.kind ?? 'service';
    if (!['service', 'document', 'issue', 'milestone'].includes(String(kind))) throw new Error(`Unknown node kind: ${String(kind)}`);
    let position: DocumentGraphNode['position'];
    if (n.position != null) {
      const p = record(n.position, 'Node position');
      if (![p.x, p.y].every(v => typeof v === 'number' && Number.isFinite(v) && Math.abs(v) <= 10000)) throw new Error('Node positions must be finite numbers between -10000 and 10000.');
      position = { x: p.x as number, y: p.y as number };
    }
    return { id, label: text(n.label, 'Node label', false, 160)!, detail: text(n.detail, 'Node detail', true), kind: kind as GraphKind, href: link(n.href, documentKey), position };
  });
  const edges = value.edges.map(raw => {
    const e = record(raw, 'Connection');
    const source = text(e.source, 'Connection source', false, 100)!;
    const target = text(e.target, 'Connection target', false, 100)!;
    if (!ids.has(source) || !ids.has(target)) throw new Error(`Connection references a missing node: ${source} → ${target}`);
    return { source, target, label: text(e.label, 'Connection label', true, 160) };
  });
  return { title: text(value.title, 'Graph title', true, 160) ?? 'Project map', description: text(value.description, 'Graph description', true), nodes, edges };
}
export function parseDocumentResources(source: string, documentKey?: string): DocumentResources {
  const value = parse(source);
  if (!Array.isArray(value.items) || !value.items.length || value.items.length > 24) throw new Error('Resource cards need 1–24 items.');
  return {
    title: text(value.title, 'Resources title', true, 160) ?? 'Linked resources',
    items: value.items.map(raw => {
      const r = record(raw, 'Resource');
      const href = link(r.href, documentKey);
      if (!href) throw new Error('Each resource needs a link.');
      const issueKey = text(r.issueKey, 'Jira key', true, 100);
      if (issueKey && !/^[A-Z][A-Z0-9]+-\d+$/.test(issueKey)) throw new Error('Use a Jira issue key such as DO-2394.');
      return { title: text(r.title, 'Resource title', false, 160)!, href, issueKey, description: text(r.description, 'Resource description', true) };
    }),
  };
}

/** Deterministic layers for DAGs; cycle members remain in a final grid. Explicit positions win. */
export function graphPositions(graph: DocumentGraph): Map<string, { x: number; y: number }> {
  const remaining = new Set(graph.nodes.map(n => n.id));
  const layers: string[][] = [];
  while (remaining.size) {
    const roots = [...remaining].filter(id => !graph.edges.some(e => e.target === id && remaining.has(e.source)));
    if (!roots.length) {
      const rest = [...remaining];
      for (let i = 0; i < rest.length; i += 4) layers.push(rest.slice(i, i + 4));
      break;
    }
    layers.push(roots);
    roots.forEach(id => remaining.delete(id));
  }
  const positions = new Map<string, { x: number; y: number }>();
  layers.forEach((ids, column) => ids.forEach((id, row) => positions.set(id, { x: column * 345, y: row * 155 })));
  graph.nodes.forEach(n => { if (n.position) positions.set(n.id, n.position); });
  return positions;
}
